import { CheckCircle2, FileUp, Info, RefreshCw, Search } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { PARTNER_NAV } from "./nav";

const STEPS = [
  {
    icon: Search,
    title: "1. Find the student",
    body:
      "Open My students. Every student assigned to you appears here automatically once their family " +
      "enrolls with Alabama Church School through your enrollment page. Use the search box to find a " +
      "student by name, grade or academic year.",
  },
  {
    icon: FileUp,
    title: "2. Open the student and upload their records",
    body:
      "Click Open next to the student, then click Upload Annual Records and choose their completed " +
      "academic records package as a PDF. One PDF per student, per academic year.",
  },
  {
    icon: CheckCircle2,
    title: "3. Watch the review status",
    body:
      "As soon as you upload, the status changes to Submitted and Alabama Church School reads the PDF " +
      "and reviews it. The status moves through Under Review, then Approved and Document Issued.",
  },
  {
    icon: RefreshCw,
    title: "4. If a correction is requested",
    body:
      "If the status shows Needs Correction, open the student to read the note from Alabama Church " +
      "School, then upload a corrected PDF to that same student. Your earlier upload is kept in the " +
      "history, so nothing is lost.",
  },
];

export default function PartnerGuide() {
  const { user } = useAuth();

  return (
    <AppShell
      nav={PARTNER_NAV}
      title="How it works"
      subtitle="Submitting annual academic records to Alabama Church School"
    >
      <div className="max-w-3xl space-y-6">
        <div
          className="flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 px-5 py-4 text-sm text-blue-900"
          data-testid="guide-intro"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <strong>My students is empty until a family enrolls.</strong>{" "}
            {user?.partner?.name || "Your organization"} cannot add students here — students are
            created when a family enrolls with Alabama Church School through your enrollment page, and
            they then appear in your list automatically.
          </div>
        </div>

        {STEPS.map((step, i) => (
          <Card key={step.title} className="border-slate-200" data-testid={`guide-step-${i}`}>
            <CardContent className="flex gap-5 p-6">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-900">
                <step.icon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-900">{step.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.body}</p>
              </div>
            </CardContent>
          </Card>
        ))}

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h2 className="text-base font-semibold text-slate-900">Good to know</h2>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li>You only ever see students assigned to your organization.</li>
              <li>Uploads must be PDF files. Every version you upload is kept in the record history.</li>
              <li>
                Once Alabama Church School issues the final academic document, you can download it
                from the student's window.
              </li>
              <li>
                Alabama Church School reviews and approves every record — nothing is approved
                automatically from an uploaded PDF.
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
